import Api from '@/api'
import Candidate from '@/models/Candidate'

// Open to Work (Sprint 1) — company-side candidate search, resume requests,
// workforce invitations, favourites and pools.
//
// D10: this module is named `candidate`, deliberately not `resume` or
// anything overlapping `store/modules/company.js`'s existing unrelated
// `resumes`/`getCompanyResumes` state (a company's own tradies' resumes).

const FILTER_KEYS = [
  'trade',
  'location',
  'availability',
  'employment_type',
  'experience_band',
  'ticket',
  'white_card',
  'driver_licence',
  'fifo',
]

function buildCandidateQuery({
  filters = {},
  page = 1,
  perPage = 20,
  sort,
} = {}) {
  const query = Candidate.page(page).limit(perPage)
  FILTER_KEYS.forEach((key) => {
    const value = filters ? filters[key] : undefined
    if (value !== undefined && value !== null && value !== '') {
      query.where(key, value)
    }
  })
  if (sort) {
    query.orderBy(sort)
  } else if (filters && filters.sort) {
    query.orderBy(filters.sort)
  }
  return query
}

export default {
  namespaced: true,

  state: {
    candidates: [],
    candidatesMeta: {},
    inProgressCandidates: [],
    currentCandidate: null,
    filters: {},
    favourites: [],
    pools: [],
    dashboardStats: null,
    pendingTokenInvitations: [],
  },

  getters: {
    candidates(state) {
      return state.candidates
    },
    candidatesMeta(state) {
      return state.candidatesMeta
    },
    inProgressCandidates(state) {
      return state.inProgressCandidates
    },
    currentCandidate(state) {
      return state.currentCandidate
    },
    filters(state) {
      return state.filters
    },
    favourites(state) {
      return state.favourites
    },
    isFavourite: (state) => (tradieId) => {
      return state.favourites.some(
        (fav) => String(fav.tradie_id || fav.id) === String(tradieId),
      )
    },
    pools(state) {
      return state.pools
    },
    dashboardStats(state) {
      return state.dashboardStats
    },
    pendingTokenInvitations(state) {
      return state.pendingTokenInvitations
    },
  },

  mutations: {
    setCandidates(state, candidates) {
      state.candidates = candidates
    },
    setCandidatesMeta(state, meta) {
      state.candidatesMeta = meta
    },
    clearCandidates(state) {
      state.candidates = []
      state.candidatesMeta = {}
    },

    setInProgressCandidates(state, candidates) {
      state.inProgressCandidates = candidates
    },

    setCurrentCandidate(state, candidate) {
      state.currentCandidate = candidate
    },
    clearCurrentCandidate(state) {
      state.currentCandidate = null
    },

    setFilters(state, filters) {
      state.filters = filters || {}
    },
    clearFilters(state) {
      state.filters = {}
    },

    setFavourites(state, favourites) {
      state.favourites = favourites
    },

    setPools(state, pools) {
      state.pools = pools
    },

    mergePoolDetail(state, poolDetail) {
      state.pools = state.pools.map((pool) =>
        pool.id === poolDetail.id ? { ...pool, ...poolDetail } : pool,
      )
    },

    setDashboardStats(state, stats) {
      state.dashboardStats = stats
    },

    setPendingTokenInvitations(state, invitations) {
      state.pendingTokenInvitations = invitations
    },
    removePendingTokenInvitation(state, invitationId) {
      state.pendingTokenInvitations = state.pendingTokenInvitations.filter(
        (inv) => String(inv.id) !== String(invitationId),
      )
    },
  },

  actions: {
    async fetchCandidates({ commit }, payload = {}) {
      commit('setFilters', payload.filters || {})
      const query = buildCandidateQuery(payload)
      return new Promise((resolve, reject) => {
        query
          .get()
          .then((res) => {
            commit('setCandidates', res.data || [])
            commit('setCandidatesMeta', res.meta || {})
            resolve(res)
          })
          .catch((err) => reject(err.response))
      })
    },

    // Candidates the company currently has an active conversation with —
    // a pending/accepted resume request, or a pending/accepted/token_pending
    // workforce invitation. The "In Progress" page.
    async fetchInProgressCandidates({ commit }) {
      return new Promise((resolve, reject) => {
        Api.get('open-to-work/candidates-in-progress')
          .then((res) => {
            const data = (res.data && res.data.data) || res.data || []
            commit('setInProgressCandidates', data)
            resolve(data)
          })
          .catch((err) => reject(err.response))
      })
    },

    // Distinct trade values actually on candidates' profiles — for the Trade
    // filter dropdown, in place of the app-wide "official" trade picker list
    // (which doesn't include free-text values a tradie typed before trade
    // was a proper dropdown). See CandidateSearchService::tradeOptions().
    async fetchCandidateTradeOptions() {
      return new Promise((resolve, reject) => {
        Api.get('open-to-work/candidates/trades')
          .then((res) => resolve((res.data && res.data.data) || res.data || []))
          .catch((err) => reject(err.response))
      })
    },

    async fetchCandidateById({ commit }, id) {
      commit('clearCurrentCandidate')
      return new Promise((resolve, reject) => {
        Api.get(`open-to-work/candidates/${id}`)
          .then((res) => {
            const data = res.data && res.data.data ? res.data.data : res.data
            commit('setCurrentCandidate', data)
            resolve(data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async fetchFavourites({ commit }) {
      return new Promise((resolve, reject) => {
        Api.get('open-to-work/favourites')
          .then((res) => {
            const data = (res.data && res.data.data) || res.data || []
            commit('setFavourites', data)
            resolve(data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async fetchPools({ commit }) {
      return new Promise((resolve, reject) => {
        Api.get('open-to-work/pools')
          .then((res) => {
            const data = (res.data && res.data.data) || res.data || []
            commit('setPools', data)
            resolve(data)
          })
          .catch((err) => reject(err.response))
      })
    },

    // GET /pools only returns { id, name, member_count } — this hydrates one
    // pool's actual member list (full candidate shape + tags/notes), merged
    // into the matching entry in state.pools.
    async fetchPoolDetail({ commit }, poolId) {
      return new Promise((resolve, reject) => {
        Api.get(`open-to-work/pools/${poolId}`)
          .then((res) => {
            const data = (res.data && res.data.data) || res.data
            commit('mergePoolDetail', data)
            resolve(data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async fetchDashboardStats({ commit }) {
      return new Promise((resolve, reject) => {
        Api.get('open-to-work/dashboard')
          .then((res) => {
            const data = (res.data && res.data.data) || res.data || null
            commit('setDashboardStats', data)
            resolve(data)
          })
          .catch((err) => reject(err.response))
      })
    },

    // NOTE: the build spec's Part A route list doesn't document a GET list
    // endpoint for workforce invitations (only the send/apply-token/
    // dismiss-token routes). This assumes a `status[]` filterable list
    // endpoint at the same resource path — flagged for confirmation with the
    // API side; one-line change if the real endpoint differs.
    async fetchPendingTokenInvitations({ commit }) {
      return new Promise((resolve, reject) => {
        Api.get('open-to-work/workforce-invitations', {
          params: { 'filter[status]': 'accepted,token_pending' },
        })
          .then((res) => {
            const data = (res.data && res.data.data) || res.data || []
            commit('setPendingTokenInvitations', data)
            resolve(data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async sendResumeRequest(_ctx, tradieId) {
      return new Promise((resolve, reject) => {
        Api.post('open-to-work/resume-requests', { tradie_id: tradieId })
          .then((res) => resolve(res.data))
          .catch((err) => reject(err.response))
      })
    },

    async sendWorkforceInvitation(_ctx, tradieId) {
      return new Promise((resolve, reject) => {
        Api.post('open-to-work/workforce-invitations', { tradie_id: tradieId })
          .then((res) => resolve(res.data))
          .catch((err) => reject(err.response))
      })
    },

    async applyToken({ commit }, invitationId) {
      return new Promise((resolve, reject) => {
        Api.post(
          `open-to-work/workforce-invitations/${invitationId}/apply-token`,
        )
          .then((res) => {
            commit('removePendingTokenInvitation', invitationId)
            resolve(res.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async dismissToken({ commit }, invitationId) {
      return new Promise((resolve, reject) => {
        Api.post(
          `open-to-work/workforce-invitations/${invitationId}/dismiss-token`,
        )
          .then((res) => {
            commit('removePendingTokenInvitation', invitationId)
            resolve(res.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    // `payload` is either a bare tradieId (legacy callers) or
    // `{ tradieId, candidate }` — `candidate` is the full candidate object the
    // caller already has in memory (e.g. CandidateCard), used as a fallback
    // so the favourites list shows real data immediately, in case the API's
    // add/remove response doesn't come back fully hydrated.
    async toggleFavourite({ commit, state }, payload) {
      const isPayloadObject =
        payload && typeof payload === 'object' && 'tradieId' in payload
      const tradieId = isPayloadObject ? payload.tradieId : payload
      const candidate = isPayloadObject ? payload.candidate : null

      const isFav = state.favourites.some(
        (fav) => String(fav.tradie_id || fav.id) === String(tradieId),
      )
      return new Promise((resolve, reject) => {
        const request = isFav
          ? Api.delete(`open-to-work/favourites/${tradieId}`)
          : Api.post(`open-to-work/favourites/${tradieId}`)

        request
          .then((res) => {
            if (isFav) {
              commit(
                'setFavourites',
                state.favourites.filter(
                  (fav) => String(fav.tradie_id || fav.id) !== String(tradieId),
                ),
              )
            } else {
              // The add endpoint returns a thin join-row (company_id,
              // tradie_id — no name/trade/etc.), not a hydrated candidate, so
              // a truthy response is not the same as a useful one. Prefer it
              // only when it's actually hydrated; otherwise use the full
              // candidate object the caller already had, and fall back to
              // the thin API row (still correct, just undisplayable) last.
              const apiCandidate = res.data && res.data.data
              const apiCandidateIsHydrated = !!(
                apiCandidate && apiCandidate.name
              )
              commit('setFavourites', [
                ...state.favourites,
                (apiCandidateIsHydrated && apiCandidate) ||
                  candidate ||
                  apiCandidate || { tradie_id: tradieId, id: tradieId },
              ])
            }
            resolve(res.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async createPool({ commit, dispatch }, name) {
      return new Promise((resolve, reject) => {
        Api.post('open-to-work/pools', { name })
          .then((res) => {
            dispatch('fetchPools')
            resolve(res.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async updatePool({ dispatch }, { id, name }) {
      return new Promise((resolve, reject) => {
        Api.put(`open-to-work/pools/${id}`, { name })
          .then((res) => {
            dispatch('fetchPools')
            resolve(res.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async deletePool({ commit, state }, id) {
      return new Promise((resolve, reject) => {
        Api.delete(`open-to-work/pools/${id}`)
          .then((res) => {
            commit(
              'setPools',
              state.pools.filter((pool) => String(pool.id) !== String(id)),
            )
            resolve(res.data)
          })
          .catch((err) => reject(err.response))
      })
    },

    async addCandidateToPool(_ctx, { poolId, tradieId, tags, notes }) {
      return new Promise((resolve, reject) => {
        Api.post(`open-to-work/pools/${poolId}/candidates`, {
          tradie_id: tradieId,
          tags,
          notes,
        })
          .then((res) => resolve(res.data))
          .catch((err) => reject(err.response))
      })
    },

    async removeCandidateFromPool(_ctx, { poolId, tradieId }) {
      return new Promise((resolve, reject) => {
        Api.delete(`open-to-work/pools/${poolId}/candidates/${tradieId}`)
          .then((res) => resolve(res.data))
          .catch((err) => reject(err.response))
      })
    },

    async updatePoolTags(_ctx, { poolId, tradieId, tags }) {
      return new Promise((resolve, reject) => {
        Api.post(`open-to-work/pools/${poolId}/tags`, {
          tradie_id: tradieId,
          tags,
        })
          .then((res) => resolve(res.data))
          .catch((err) => reject(err.response))
      })
    },

    async addPoolNote(_ctx, { poolId, tradieId, note }) {
      return new Promise((resolve, reject) => {
        // API validates/reads `notes` (plural) — see PoolController::addNote.
        // Kept the action's own param name `note` (singular) since it's
        // already what PoolManagementList.vue passes in; only the outgoing
        // body key needed to match the API contract.
        Api.post(`open-to-work/pools/${poolId}/notes`, {
          tradie_id: tradieId,
          notes: note,
        })
          .then((res) => resolve(res.data))
          .catch((err) => reject(err.response))
      })
    },
  },
}
